import { useEffect } from 'react'

import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'

import { apiGetAllTokens } from '@src/api/machprotocol.api'
import {
    ChainId,
    ChainName,
    NATIVE_TOKEN_ADDRESS,
    WRAPPED_GAS_TOKENS,
    getChainId,
    getGasTokenSymbol,
} from '@src/configs/chainInfo'
import { isV2SupportedChain } from '@src/configs/chainInfo'
import { HARDCODED_TESTNET_TOKEN_DATA, PUBLIC_STATIC_LOGOS } from '@src/configs/tokens'
import { TOKENS_REFRESH_INTERVAL } from '@src/constants/application'
import { HARDCODED_TESTNET_TOKENS } from '@src/constants/tokens'
import { Address, IToken } from '@src/types/token'
import { bigintPow, createContextLogger } from '@src/utils'
import { isFeatureEnabled } from '@src/utils/environment'
import { useTokensStore } from '@src/zustand_stores'

const logger = createContextLogger('useFetchAllTokens')

const loadAllTokens = async (): Promise<{ tokens: IToken[]; nativeTokenPricesInUSD: Record<number, number> }> => {
    logger.info('🔄 Starting token loading process...')

    const tokens =
        isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY') ? HARDCODED_TESTNET_TOKENS : []

    const nativeTokenPricesInUSD: Record<number, number> = {}

    try {
        logger.info('📡 Fetching tokens from API...')
        const loadedTokens = await apiGetAllTokens()
        logger.info('📦 Received tokens data:', loadedTokens)

        if (isFeatureEnabled('ENABLE_FEATHER')) {
            const bitcoinToken: IToken = {
                name: 'Bitcoin',
                symbol: 'BTC',
                address: '0x0000000000000000000000000000000000000000' as Address, // Using null address for Bitcoin
                decimals: 8,
                chainId: ChainId.Bitcoin,
                logoURI: '/assets/token/btc.png',
                native: true,
                wrapped: false,
                isStableCoin: false,
                userBalance: undefined,
                userBalanceInUSD: undefined,
                market_cap: 1000000000000, // High market cap value to make it show near the top
            }
            tokens.push(bitcoinToken)
        }

        for (const _networkName of Object.keys(loadedTokens)) {
            const networkName = _networkName as ChainName
            const loadedTokenData = loadedTokens[networkName]
            const chainId = getChainId(networkName)
            nativeTokenPricesInUSD[chainId] = loadedTokenData.native.current_price // Add native price to the list even if chain is feature gated.

            const nativeTokenAddress = NATIVE_TOKEN_ADDRESS[chainId]
            const gasTokenSymbol = getGasTokenSymbol(networkName)
            const wrappedGasToken = WRAPPED_GAS_TOKENS[chainId]
            const filterSol = isFeatureEnabled('ONLY_SOL_USDC') && chainId === ChainId.Solana

            if (!isV2SupportedChain(chainId)) {
                continue
            }

            if (!isFeatureEnabled('SHOW_TRON_CHAIN') && chainId === ChainId.Tron) {
                continue
            }

            // Add native gas & wrapped native gas tokens
            if (loadedTokenData.native) {
                // FE-546
                // NOTE: we DO NOT want to support: wrapping/unwrapping on CELO
                // CELO is a modified EVM chain wherein the celo erc20 token is 1:1 with
                // the native gas asset without wrapping/unwrapping
                // therefore we add the erc20 token as the native asset and we do not add any WCELO token.
                if (networkName == 'celo') {
                    tokens.push({
                        symbol: `${gasTokenSymbol}`,
                        logoURI: `/assets/token/${gasTokenSymbol.toLowerCase()}.png`,
                        name: `${gasTokenSymbol}`,
                        chainId: chainId,
                        address: nativeTokenAddress,
                        decimals: loadedTokenData.native.decimals,
                        native: true,
                        wrapped: false,
                        isStableCoin: false,
                        price_change_24h: loadedTokenData.native.price_change_24h,
                        price_change_percentage_24h: loadedTokenData.native.price_change_percentage_24h,
                        price_current: loadedTokenData.native.current_price,
                        market_cap: loadedTokenData.native.market_cap,
                    })
                } else {
                    tokens.push({
                        symbol: `${gasTokenSymbol}`,
                        logoURI: `/assets/token/${gasTokenSymbol.toLowerCase()}.png`,
                        name: `${gasTokenSymbol}`,
                        chainId: chainId,
                        address: nativeTokenAddress,
                        decimals: loadedTokenData.native.decimals,
                        native: true,
                        wrapped: false,
                        isStableCoin: false,
                        price_change_24h: loadedTokenData.native.price_change_24h,
                        price_change_percentage_24h: loadedTokenData.native.price_change_percentage_24h,
                        price_current: loadedTokenData.native.current_price,
                        market_cap: loadedTokenData.native.market_cap,
                    })

                    // Add wrapped gas token if available
                    if (wrappedGasToken && wrappedGasToken.address && !filterSol) {
                        tokens.push({
                            symbol: `W${gasTokenSymbol}`,
                            logoURI: `/assets/token/w${gasTokenSymbol.toLowerCase()}.png`,
                            name: `W${gasTokenSymbol}`,
                            chainId: chainId,
                            address: wrappedGasToken.address,
                            decimals: loadedTokenData.native.decimals,
                            native: false,
                            wrapped: true,
                            isStableCoin: false,
                            price_change_24h: loadedTokenData.native.price_change_24h,
                            price_change_percentage_24h: loadedTokenData.native.price_change_percentage_24h,
                            price_current: loadedTokenData.native.current_price,
                            market_cap: loadedTokenData.native.market_cap,
                        })
                    }
                }
            }

            for (const tokenAddress of Object.keys(loadedTokenData)) {
                try {
                    if (
                        tokenAddress !== 'native' &&
                        wrappedGasToken &&
                        wrappedGasToken.address.toLowerCase() !== tokenAddress.toLowerCase()
                    ) {
                        // If ONLY_SOL_USDC is enabled, only add USDC token
                        if (isFeatureEnabled('ONLY_SOL_USDC') && chainId === ChainId.Solana) {
                            const isUSDC = loadedTokenData[tokenAddress].symbol.toUpperCase() === 'USDC'
                            if (!isUSDC) {
                                continue
                            }
                        }

                        let logo: string = ''

                        const publicLogoKey = PUBLIC_STATIC_LOGOS[networkName]
                            ? Object.keys(PUBLIC_STATIC_LOGOS[networkName]).find(o => {
                                  o.toLowerCase() == tokenAddress.toLowerCase()
                              })
                            : null

                        if (publicLogoKey) {
                            logo = PUBLIC_STATIC_LOGOS[networkName][publicLogoKey]
                        } else {
                            logo = loadedTokenData[tokenAddress].image ?? ''
                        }

                        // Do not add tokens with missing decimals.
                        if (
                            typeof loadedTokenData[tokenAddress].decimals != 'number' ||
                            loadedTokenData[tokenAddress].decimals < 0
                        ) {
                            logger.warn('Bad token data from server: token has no decimals or decimals < 0', {
                                [networkName]: {
                                    [tokenAddress]: loadedTokenData[tokenAddress],
                                },
                            })
                        } else {
                            const isTokenStableCoin = isStableCoin(loadedTokenData[tokenAddress].symbol.toUpperCase())
                            tokens.push({
                                symbol: loadedTokenData[tokenAddress].symbol.toUpperCase(),
                                logoURI: logo,
                                name: loadedTokenData[tokenAddress].name,
                                chainId: chainId,
                                address: tokenAddress as Address,
                                decimals: loadedTokenData[tokenAddress].decimals,
                                native: false,
                                wrapped: false,
                                isStableCoin: isTokenStableCoin,
                                price_change_24h: loadedTokenData[tokenAddress].price_change_24h,
                                price_change_percentage_24h: loadedTokenData[tokenAddress].price_change_percentage_24h,
                                price_current: loadedTokenData[tokenAddress].current_price,
                                market_cap: loadedTokenData[tokenAddress].market_cap,
                            })
                        }
                    }
                } catch (e) {
                    logger.error('Error processing token', { error: e })
                }
            }
        }

        return { tokens: tokens, nativeTokenPricesInUSD: nativeTokenPricesInUSD }
    } catch (error) {
        const errorDetails = {
            message: (error as Error).message,
            stack: (error as Error).stack,
            name: (error as Error).name,
            response: (error as any).response?.data,
            status: (error as any).response?.status,
            headers: (error as any).response?.headers,
        }

        // Send to Sentry with additional context
        Sentry.withScope(scope => {
            scope.setTag('feature', 'token_loading')
            scope.setLevel('error')
            scope.setContext('api_response', {
                response: errorDetails.response,
                status: errorDetails.status,
                headers: errorDetails.headers,
            })
            scope.setContext('state', {
                currentTokens: tokens.length,
            })
            Sentry.captureException(error)
        })

        logger.error('Failed to load tokens', { error: errorDetails })
        logger.error('🔍 Full error object:', error)

        // If API fails, fall back to hardcoded data for testnets
        if (isFeatureEnabled('ENABLE_TESTNETS') || isFeatureEnabled('USE_TESTNETS_ONLY')) {
            logger.info('🧪 API failed, falling back to hardcoded testnet data')
            return loadHardcodedTestnetTokens()
        }

        throw error
    }
}

const isStableCoin = (symbol: string): boolean => {
    const stableCoinSymbols = [
        'USDC',
        'USDT',
        'BUSD',
        'DAI',
        'USDM',
        'USDTO',
        'USDT0',
        'USDC.e',
        'USDT.e',
        'USDC.PAX',
        'USDT.PAX',
        'USDC.G',
        'USDT.G',
        'USDC.USDT',
        'USDT.USDC',
        'USDC.USDT.e',
        'USDT.USDC.e',
        'USDC.USDT.PAX',
        'USDT.USDC.PAX',
        'USDC.USDT.G',
        'USDT.USDC.G',
    ]
    return stableCoinSymbols.includes(symbol.toUpperCase())
}

/**
 * Helper function to load tokens from static data for testnets
 */
const loadHardcodedTestnetTokens = (): { tokens: IToken[]; nativeTokenPricesInUSD: Record<number, number> } => {
    logger.info('Loading static testnet token data')

    const tokens: IToken[] = []
    const nativeTokenPricesInUSD: Record<number, number> = {}

    // Add Bitcoin token for testnets if ENABLE_FEATHER is enabled
    if (isFeatureEnabled('ENABLE_FEATHER')) {
        const bitcoinToken: IToken = {
            name: 'Bitcoin',
            symbol: 'BTC',
            address: '0x0000000000000000000000000000000000000000' as Address,
            decimals: 8,
            chainId: ChainId.Bitcoin,
            logoURI: '/assets/token/btc.png',
            native: true,
            wrapped: false,
            isStableCoin: false,
            market_cap: 1000000000000,
            // Dummy balance for testnet
            userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(8)),
            userBalanceInUSD: 5000000,
        }
        tokens.push(bitcoinToken)
    }

    // Process each testnet in HARDCODED_TESTNET_TOKEN_DATA
    for (const _networkName of Object.keys(HARDCODED_TESTNET_TOKEN_DATA)) {
        const networkName = _networkName as ChainName
        const staticData = HARDCODED_TESTNET_TOKEN_DATA[networkName]
        const chainId = getChainId(networkName)

        const wrappedGasToken = WRAPPED_GAS_TOKENS[chainId]
        const gasTokenSymbol = getGasTokenSymbol(networkName)

        // Add native token price
        if (staticData.native) {
            nativeTokenPricesInUSD[chainId] = staticData.native.current_price
        }

        if (!isV2SupportedChain(chainId)) {
            continue
        }

        // Add native gas token
        if (staticData.native) {
            tokens.push({
                symbol: `${gasTokenSymbol}`,
                logoURI: `/assets/token/${gasTokenSymbol.toLowerCase()}.png`,
                name: `${gasTokenSymbol}`,
                chainId: chainId,
                address: NATIVE_TOKEN_ADDRESS[chainId],
                decimals: staticData.native.decimals,
                native: true,
                wrapped: false,
                isStableCoin: false,
                price_change_24h: staticData.native.price_change_24h,
                price_change_percentage_24h: staticData.native.price_change_percentage_24h,
                price_current: staticData.native.current_price,
                market_cap: staticData.native.market_cap,

                // Dummy balance
                userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                userBalanceInUSD: 1000,
            })

            // Add wrapped gas token if available
            if (wrappedGasToken && wrappedGasToken.address) {
                tokens.push({
                    symbol: `W${gasTokenSymbol}`,
                    logoURI: `/assets/token/w${gasTokenSymbol.toLowerCase()}.png`,
                    name: `W${gasTokenSymbol}`,
                    chainId: chainId,
                    address: wrappedGasToken.address,
                    decimals: staticData.native.decimals,
                    native: false,
                    wrapped: true,
                    isStableCoin: false,
                    price_change_24h: staticData.native.price_change_24h,
                    price_change_percentage_24h: staticData.native.price_change_percentage_24h,
                    price_current: staticData.native.current_price,
                    market_cap: staticData.native.market_cap,

                    // Dummy balance
                    userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                    userBalanceInUSD: 1000,
                })
            }
        }

        // Add other tokens from static data
        if (staticData.assets) {
            for (const tokenAddress of Object.keys(staticData.assets)) {
                const tokenData = staticData.assets[tokenAddress]

                // Skip if this is the wrapped token we already added
                if (wrappedGasToken && wrappedGasToken.address.toLowerCase() === tokenAddress.toLowerCase()) {
                    continue
                }

                const isTokenStableCoin = isStableCoin(tokenData.symbol.toUpperCase())
                tokens.push({
                    symbol: tokenData.symbol.toUpperCase(),
                    logoURI: tokenData.image || '',
                    name: tokenData.name,
                    chainId: chainId,
                    address: tokenAddress as Address,
                    decimals: tokenData.decimals,
                    native: false,
                    wrapped: false,
                    isStableCoin: isTokenStableCoin,
                    price_change_24h: tokenData.price_change_24h,
                    price_change_percentage_24h: tokenData.price_change_percentage_24h,
                    price_current: tokenData.current_price,
                    market_cap: tokenData.market_cap,

                    // Dummy balance
                    userBalance: BigInt(100) * bigintPow(BigInt(10), BigInt(6)),
                    userBalanceInUSD: 1000,
                })
            }
        }
    }

    logger.info(`Loaded ${tokens.length} static testnet tokens`)
    return { tokens, nativeTokenPricesInUSD }
}

/**
 * Tanstack query for fetching tokens
 */
function useAllTokensQuery() {
    return useQuery({
        queryKey: ['allTokens'],
        // Function to fetch data.
        // loadAllTokens resolves in an object of type { tokens: IToken[], nativeTokenPricesInUSD: Record<number, number> }
        queryFn: loadAllTokens,
        staleTime: TOKENS_REFRESH_INTERVAL,
        refetchInterval: TOKENS_REFRESH_INTERVAL,
        refetchOnWindowFocus: false,
    })
}

export function useFetchAllTokens() {
    const { data, isLoading, error } = useAllTokensQuery()

    const existingTokens = useTokensStore(state => state.tokens)

    const setTokens = useTokensStore(state => state.setTokens)
    const setNativeTokenPricesInUSD = useTokensStore(state => state.setNativeTokenPricesInUSD)
    const setStoreLoading = useTokensStore(state => state.setIsLoading)
    const setStoreError = useTokensStore(state => state.setError)

    useEffect(() => {
        // Sync loading & error states
        setStoreLoading(isLoading)
        setStoreError(error ? (error as Error).message : null)

        if (data) {
            // Merge existing tokens with new tokens, preserving user balances
            const mergedTokens = data.tokens.map(newToken => {
                const existing = existingTokens.find(
                    // Sometimes I got undefined has no function `toLowerCase`
                    t =>
                        t.address &&
                        newToken.address &&
                        t.address.toLowerCase() === newToken.address.toLowerCase() &&
                        t.chainId === newToken.chainId
                )

                return {
                    ...newToken,
                    // Preserve existing userBalance & userBalanceInUSD if available
                    userBalance: existing?.userBalance,
                    userBalanceInUSD: existing?.userBalanceInUSD,
                }
            })

            setTokens(mergedTokens)
            setNativeTokenPricesInUSD(data.nativeTokenPricesInUSD)
        }
    }, [data, isLoading, error, setTokens, setNativeTokenPricesInUSD, setStoreLoading, setStoreError])

    /**
     * @note We do not return `data` here; data is consumed via the Zustand store.
     */
    return { isLoading, error }
}
